import { EvalMetricsCard } from "@/components/eval/EvalMetricsCard";
import { EvalMetricsChart } from "@/components/eval/EvalMetricsChart";
import { EvalStatusBadge } from "@/components/eval/EvalStatusBadge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table";
import { AdminService } from "@/gen/omnios/v1/admin_connect.js";
import { transport } from "@/lib/grpc";
import { createClient } from "@connectrpc/connect";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

export const Route = createFileRoute("/admin/eval/")({
	component: EvalDashboard,
});

const adminClient = createClient(AdminService, transport);

function EvalDashboard() {
	const { t } = useTranslation("eval");
	const navigate = useNavigate();

	const { data: runsData, isLoading } = useQuery({
		queryKey: ["admin", "eval-runs"],
		queryFn: () =>
			adminClient.listAllEvalRuns({
				pageSize: 20,
				statusFilter: "",
			}),
	});

	const completedRuns =
		runsData?.runs?.filter((r) => r.status === "completed") ?? [];
	const latestCompleted = completedRuns[0];
	const previousCompleted = completedRuns[1];

	return (
		<div className="space-y-6">
			{isLoading ? (
				<div className="grid gap-4 md:grid-cols-3">
					<Skeleton className="h-32" />
					<Skeleton className="h-32" />
					<Skeleton className="h-32" />
				</div>
			) : latestCompleted?.metrics ? (
				<EvalMetricsCard
					metrics={latestCompleted.metrics}
					previousMetrics={previousCompleted?.metrics}
				/>
			) : (
				<Card>
					<CardContent className="py-8 text-center text-muted-foreground">
						{t("no_completed_runs")}
					</CardContent>
				</Card>
			)}

			{runsData?.runs && runsData.runs.length >= 2 && (
				<EvalMetricsChart
					runs={runsData.runs}
					onRunClick={(id) =>
						navigate({ to: "/admin/eval/runs/$id", params: { id } })
					}
				/>
			)}

			<Card>
				<CardHeader>
					<CardTitle className="text-base">{t("recent_runs")}</CardTitle>
				</CardHeader>
				<CardContent>
					{isLoading ? (
						<Skeleton className="h-48" />
					) : !runsData?.runs?.length ? (
						<p className="text-sm text-muted-foreground">{t("no_runs")}</p>
					) : (
						<Table>
							<TableHeader>
								<TableRow>
									<TableHead>{t("col_dataset")}</TableHead>
									<TableHead>{t("col_version")}</TableHead>
									<TableHead>{t("col_mode")}</TableHead>
									<TableHead>{t("col_model")}</TableHead>
									<TableHead>{t("col_status")}</TableHead>
									<TableHead>Hit@1</TableHead>
									<TableHead>MRR</TableHead>
									<TableHead>{t("col_date")}</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{runsData.runs.map((run) => (
									<TableRow
										key={run.id}
										className="cursor-pointer hover:bg-muted/50"
										onClick={() =>
											navigate({
												to: "/admin/eval/runs/$id",
												params: { id: run.id },
											})
										}
									>
										<TableCell className="font-medium">
											{run.datasetName || run.datasetId.slice(0, 8)}
										</TableCell>
										<TableCell className="font-mono text-xs">
											{run.algorithmVersion || "—"}
										</TableCell>
										<TableCell className="text-xs">{run.matchMode}</TableCell>
										<TableCell className="text-xs">
											{run.modelName || "—"}
										</TableCell>
										<TableCell>
											<EvalStatusBadge status={run.status} />
										</TableCell>
										<TableCell className="font-mono">
											{run.metrics
												? `${(run.metrics.hitAt1 * 100).toFixed(1)}%`
												: "—"}
										</TableCell>
										<TableCell className="font-mono">
											{run.metrics ? run.metrics.mrr.toFixed(3) : "—"}
										</TableCell>
										<TableCell className="text-xs text-muted-foreground">
											{run.createdAt
												? new Date(
														Number(run.createdAt.seconds) * 1000,
													).toLocaleDateString()
												: "—"}
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					)}
				</CardContent>
			</Card>
		</div>
	);
}
